/**
 * Mock data for Kenbri Group Analytics
 *
 * This file provides lightweight, typed stubs for all assurance features.
 * In production, these would be replaced with real API calls.
 */

import type {
  AlignedSignal,
  InstitutionRow,
  TrailData,
  QuickLookData,
  TimelineSnapshot,
  CompareDiff,
  SearchResult,
  Topic,
} from '../types/assurance';

/**
 * Mock Aligned Signals
 * Represents current state across key regulatory topics
 */
export const mockAlignedSignals: AlignedSignal[] = [
  {
    topic: 'Model',
    value: 87,
    confidence: 0.92,
    updatedAt: '2025-11-12T14:23:00Z',
    spark: [82, 84, 83, 85, 86, 87, 88, 87],
  },
  {
    topic: 'Liquidity',
    value: 94,
    confidence: 0.88,
    updatedAt: '2025-11-12T14:15:00Z',
    spark: [91, 92, 93, 94, 93, 94, 95, 94],
  },
  {
    topic: 'Operational',
    value: 79,
    confidence: 0.85,
    updatedAt: '2025-11-12T13:45:00Z',
    spark: [75, 76, 78, 79, 78, 79, 80, 79],
  },
];

/**
 * Mock Institutions
 * Represents financial institutions under portfolio oversight
 */
export const mockInstitutions: InstitutionRow[] = [
  {
    id: 'inst_001',
    name: 'Northern Maple Financial',
    status: 'calm',
    trend: [85, 86, 87, 88, 89, 90],
    updatedAt: '2025-11-12T14:20:00Z',
  },
  {
    id: 'inst_002',
    name: 'Pacific Coast Bank',
    status: 'watch',
    trend: [90, 88, 85, 82, 80, 78],
    updatedAt: '2025-11-12T14:18:00Z',
  },
  {
    id: 'inst_003',
    name: 'Great Lakes Trust',
    status: 'calm',
    trend: [92, 93, 93, 94, 94, 95],
    updatedAt: '2025-11-12T14:15:00Z',
  },
  {
    id: 'inst_004',
    name: 'Atlantic Financial Group',
    status: 'action',
    trend: [78, 75, 72, 68, 65, 62],
    updatedAt: '2025-11-12T14:25:00Z',
  },
  {
    id: 'inst_005',
    name: 'Prairie Capital Bank',
    status: 'calm',
    trend: [88, 89, 89, 90, 91, 91],
    updatedAt: '2025-11-12T14:10:00Z',
  },
  {
    id: 'inst_006',
    name: 'Maritime Savings',
    status: 'watch',
    trend: [82, 81, 80, 79, 78, 77],
    updatedAt: '2025-11-12T14:22:00Z',
  },
];

/**
 * Mock Quick Look Data
 * Provides contextual information for hover/long-press interactions
 */
export const mockQuickLookData: Record<Topic, QuickLookData> = {
  Model: {
    title: 'Model Risk Assessment',
    how: 'Combines validation status, performance metrics, and tier classification across all model inventories',
    latestChange: 'Credit Risk Scorecard v2.1 validated (Nov 12, 14:23)',
  },
  Liquidity: {
    title: 'Liquidity Coverage',
    how: 'Aggregates LCR, NSFR, and stress test results with regulatory thresholds',
    latestChange: 'P3 return submitted successfully (Nov 12, 14:15)',
  },
  Operational: {
    title: 'Operational Resilience',
    how: 'Tracks incident response, business continuity, and third-party readiness',
    latestChange: 'DR test completed with 98% success (Nov 12, 13:45)',
  },
  Climate: {
    title: 'Climate Risk Exposure',
    how: 'Evaluates physical and transition risks across portfolio with scenario analysis',
    latestChange: 'B-15 scenario update: 2°C warming baseline (Nov 12, 12:30)',
  },
  Cyber: {
    title: 'Cyber Security Posture',
    how: 'Monitors controls, incidents, and third-party technology risks per B-13',
    latestChange: 'Phishing simulation: 94% detection rate (Nov 12, 11:15)',
  },
  ThirdParty: {
    title: 'Third-Party Risk',
    how: 'Assesses vendor criticality, due diligence status, and ongoing monitoring',
    latestChange: 'CloudSecure SOC 2 renewal completed (Nov 12, 10:00)',
  },
};

/**
 * Mock Trail Data
 * Provides explainability for each metric
 */
export const mockTrailData: Record<Topic, TrailData> = {
  Model: {
    ref: 'REF:E23:ModelInventory:v1.7',
    definition:
      'Model Risk Assessment reflects the health of your model risk management program, including validation status, performance monitoring, and governance compliance.',
    build: [
      'Aggregate validation status across 25 models (Tier 1/2/3)',
      'Calculate performance metrics: accuracy, drift, PSI scores',
      'Check governance: approval workflows, documentation completeness',
      'Weight by tier: Tier 1 (60%), Tier 2 (30%), Tier 3 (10%)',
      'Apply confidence based on data recency and validation frequency',
    ],
    footnotes: [
      'Version: 1.7.0',
      'Last updated: Nov 12, 2025 14:23 UTC',
      'Reviewer: Model Risk Team',
      'Methodology: Industry Best Practice',
    ],
  },
  Liquidity: {
    ref: 'REF:LIQUIDITY:LCR_NSFR:v2.3',
    definition:
      'Liquidity Coverage measures your ability to meet short-term obligations and maintain stable funding, based on LCR, NSFR, and stress testing.',
    build: [
      'Calculate Liquidity Coverage Ratio (LCR): HQLA / Net Cash Outflows',
      'Calculate Net Stable Funding Ratio (NSFR): Available / Required',
      'Run stress scenarios: market disruption, deposit run, funding freeze',
      'Compare to industry standards: LCR ≥ 100%, NSFR ≥ 100%',
      'Factor in buffer strategy and management actions',
    ],
    footnotes: [
      'Version: 2.3.1',
      'Last updated: Nov 12, 2025 14:15 UTC',
      'Reviewer: Treasury Risk Team',
      'Methodology: Industry Standard',
    ],
  },
  Operational: {
    ref: 'REF:OPERATIONAL:BCP_DR:v1.4',
    definition:
      'Operational Resilience assesses your ability to prevent, respond to, and recover from operational disruptions including cyber incidents and third-party failures.',
    build: [
      'Track incident response metrics: detection time, resolution time, impact',
      'Evaluate business continuity: DR tests, RTO/RPO achievement',
      'Assess third-party resilience: vendor readiness, exit planning',
      'Monitor control effectiveness: SOC 2, ISO 27001 compliance',
      'Weight by criticality: Tier 1 critical services prioritized',
    ],
    footnotes: [
      'Version: 1.4.2',
      'Last updated: Nov 12, 2025 13:45 UTC',
      'Reviewer: Operational Risk Team',
      'Methodology: Security Framework',
    ],
  },
  Climate: {
    ref: 'REF:B15:CLIMATE_RISK:v1.1',
    definition:
      'Climate Risk Exposure evaluates physical and transition risks across your portfolio, incorporating scenario analysis aligned with industry standards.',
    build: [
      'Identify climate-sensitive exposures by sector and geography',
      'Run scenario analysis: 1.5°C, 2°C, 3°C warming pathways',
      'Quantify physical risks: floods, wildfires, extreme weather',
      'Quantify transition risks: policy changes, technology shifts, market revaluation',
      'Aggregate impact: credit losses, operational disruption, reputation',
    ],
    footnotes: [
      'Version: 1.1.0',
      'Last updated: Nov 12, 2025 12:30 UTC',
      'Reviewer: Climate Risk Committee',
      'Methodology: Industry Climate Framework',
    ],
  },
  Cyber: {
    ref: 'REF:B13:CYBER_SECURITY:v2.0',
    definition:
      'Cyber Security Posture measures your ability to prevent, detect, and respond to cyber threats following security best practices.',
    build: [
      'Evaluate preventive controls: firewalls, email filtering, access management',
      'Track detection capabilities: SIEM, threat intelligence, monitoring',
      'Assess response readiness: incident response plan, recovery time',
      'Monitor vulnerabilities: patching cadence, penetration test findings',
      'Integrate third-party risk: vendor security assessments',
    ],
    footnotes: [
      'Version: 2.0.1',
      'Last updated: Nov 12, 2025 11:15 UTC',
      'Reviewer: CISO Office',
      'Methodology: NIST CSF',
    ],
  },
  ThirdParty: {
    ref: 'REF:B10:THIRD_PARTY:v1.5',
    definition:
      'Third-Party Risk assesses vendor management across criticality, due diligence, ongoing monitoring, and contingency planning per vendor management standards.',
    build: [
      'Classify vendors by criticality: High, Medium, Low impact',
      'Track due diligence: onboarding assessments, renewals, certifications',
      'Monitor performance: SLA compliance, incident tracking, audits',
      'Evaluate contingency: exit strategies, alternative providers, transition plans',
      'Weight by service importance: Critical services have higher impact',
    ],
    footnotes: [
      'Version: 1.5.0',
      'Last updated: Nov 12, 2025 10:00 UTC',
      'Reviewer: Third-Party Risk Team',
      'Methodology: Vendor Management Standard',
    ],
  },
};

/**
 * Mock Timeline Snapshots
 * Historical bookmarks for temporal navigation
 */
export const mockTimelineSnapshots: TimelineSnapshot[] = [
  {
    id: 'snap_001',
    label: 'Q3 2025 Close',
    timestamp: '2025-09-30T23:59:59Z',
    signals: [
      {
        topic: 'Model',
        value: 85,
        confidence: 0.90,
        updatedAt: '2025-09-30T23:59:59Z',
        spark: [80, 81, 83, 84, 84, 85],
      },
      {
        topic: 'Liquidity',
        value: 92,
        confidence: 0.87,
        updatedAt: '2025-09-30T23:59:59Z',
        spark: [90, 91, 91, 92, 92, 92],
      },
      {
        topic: 'Operational',
        value: 77,
        confidence: 0.83,
        updatedAt: '2025-09-30T23:59:59Z',
        spark: [74, 75, 76, 76, 77, 77],
      },
    ],
    institutions: mockInstitutions,
  },
];

/**
 * Mock Compare Diffs
 * Example differences for Compare Mode
 */
export const mockCompareDiffs: CompareDiff[] = [
  {
    field: 'Model Validation Status',
    before: '23/25 validated',
    after: '25/25 validated',
    delta: 2,
    significance: 'high',
  },
  {
    field: 'Liquidity Coverage Ratio',
    before: 118,
    after: 122,
    delta: 4,
    significance: 'medium',
  },
  {
    field: 'Open Cyber Incidents',
    before: 3,
    after: 1,
    delta: -2,
    significance: 'high',
  },
  {
    field: 'Third-Party Assessments Overdue',
    before: 2,
    after: 0,
    delta: -2,
    significance: 'medium',
  },
];

/**
 * Mock Search Results
 * For Command Palette search functionality
 */
export const mockSearchResults: SearchResult[] = [
  {
    id: 'search_001',
    type: 'institution',
    title: 'Northern Maple Financial',
    subtitle: 'Schedule I Bank • Status: Calm',
    path: '/institutions/inst_001',
  },
  {
    id: 'search_002',
    type: 'topic',
    title: 'Model Risk Assessment',
    subtitle: 'E-23 • 25 models • 87/100',
    path: '/dashboard?focus=model',
  },
  {
    id: 'search_003',
    type: 'definition',
    title: 'Liquidity Coverage Ratio (LCR)',
    subtitle: 'Basel III metric • Minimum 100%',
    path: '/definitions/lcr',
  },
  {
    id: 'search_004',
    type: 'institution',
    title: 'Atlantic Financial Group',
    subtitle: 'Schedule I Bank • Status: Action Required',
    path: '/institutions/inst_004',
  },
];

/**
 * Helper function to get institution by ID
 */
export function getInstitutionById(id: string): InstitutionRow | undefined {
  return mockInstitutions.find((inst) => inst.id === id);
}

/**
 * Helper function to get signal by topic
 */
export function getSignalByTopic(topic: Topic): AlignedSignal | undefined {
  return mockAlignedSignals.find((signal) => signal.topic === topic);
}

/**
 * Helper function to search across all entities
 */
export function search(query: string): SearchResult[] {
  const lowerQuery = query.toLowerCase();
  return mockSearchResults.filter(
    (result) =>
      result.title.toLowerCase().includes(lowerQuery) ||
      result.subtitle.toLowerCase().includes(lowerQuery)
  );
}
